// @ts-check
// Aba Arquivos (doc 10, seção 10): documentos do registro no R2 (objeto attachment com o alvo target<Objeto>).
// Enviar (botão ou arrastar e soltar, vários de uma vez), pré-visualizar imagem, PDF, texto, áudio e vídeo, baixar,
// renomear e excluir; ao excluir, o arquivo some do R2 quando nada mais o usa.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findManyRecords, updateOneRecord } from '../../data/rest.js';
import { createRecord, mutationCounterState } from '../../data/record-store.js';
import { uploadFile } from '../../data/conversations.js';
import { deleteUploadedFile } from '../../data/parity.js';
import { api } from '../../data/api-client.js';
import { getRelationIdFieldName, isManyToOneRelation } from '../../lib/field-metadata.js';
import { formatDate } from '../../lib/format.js';
import { Icon } from '../../ui/icon.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

// mesmo limite do upload da API (src/channels/file-storage.js)
const MAXIMUM_FILE_BYTES = 25 * 1024 * 1024;

/** Categoria, ícone e cor por extensão (doc 10, 10.2). */
const FILE_CATEGORIES = [
  { category: 'TEXT_DOCUMENT', icon: 'IconFileText', color: 'blue', extensions: ['doc', 'docm', 'docx', 'dot', 'dotx', 'odt', 'pdf', 'txt', 'rtf', 'ps', 'tex', 'pages'] },
  { category: 'SPREADSHEET', icon: 'IconTable', color: 'turquoise', extensions: ['xls', 'xlsb', 'xlsm', 'xlsx', 'xltx', 'csv', 'tsv', 'ods', 'numbers'] },
  { category: 'PRESENTATION', icon: 'IconPresentation', color: 'orange', extensions: ['ppt', 'pptx', 'potx', 'odp', 'html', 'key', 'kth'] },
  { category: 'IMAGE', icon: 'IconPhoto', color: 'yellow', extensions: ['png', 'jpg', 'jpeg', 'svg', 'gif', 'webp', 'heif', 'tif', 'tiff', 'bmp', 'ico'] },
  { category: 'VIDEO', icon: 'IconVideo', color: 'purple', extensions: ['mp4', 'avi', 'mov', 'wmv', 'mpg', 'mpeg'] },
  { category: 'AUDIO', icon: 'IconHeadphones', color: 'pink', extensions: ['mp3', 'wav', 'ogg', 'wma', 'm4a', 'opus'] },
  { category: 'ARCHIVE', icon: 'IconFileZip', color: 'gray', extensions: ['zip', 'tar', 'iso', 'gz', 'rar', '7z'] },
];
// o navegador mostra estes sem executar nada; SVG e HTML saem como download pela API
const PREVIEWABLE = { IMAGE: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'], PDF: ['pdf'], TEXT: ['txt'], VIDEO: ['mp4', 'webm'], AUDIO: ['mp3', 'ogg', 'wav', 'm4a', 'opus'] };

/** @param {string} name */
export function getFileExtension(name) {
  const index = name.lastIndexOf('.');
  return index > 0 ? name.slice(index + 1).toLowerCase() : '';
}

/** @param {string} extension */
export function getFileCategory(extension) {
  return FILE_CATEGORIES.find((candidate) => candidate.extensions.includes(extension)) ?? { category: 'OTHER', icon: 'IconFile', color: 'gray', extensions: [] };
}

/** @param {string} extension @returns {'IMAGE' | 'PDF' | 'TEXT' | 'VIDEO' | 'AUDIO' | null} */
export function getPreviewKind(extension) {
  for (const [kind, extensions] of Object.entries(PREVIEWABLE)) if (extensions.includes(extension)) return /** @type {any} */ (kind);
  return null;
}

/** @param {any} attachment */
const fileOf = (attachment) => (Array.isArray(attachment.file) ? attachment.file[0] : null);
/** @param {any} file */
const fileUrl = (file) => file?.url && !String(file.url).startsWith('/rest/') ? file.url : `/rest/files/${file?.fileId}`;

/**
 * Campo de alvo do documento para o objeto (ex.: targetPerson), ou null quando o objeto não recebe documentos.
 * @param {string} objectNameSingular
 */
export function getAttachmentTargetField(objectNameSingular) {
  const attachmentObject = objectsByNameSingular.value.get('attachment');
  const field = attachmentObject?.fields.find((candidate) => isManyToOneRelation(candidate) && candidate.relation?.targetObjectNameSingular === objectNameSingular);
  return attachmentObject && field ? { attachmentObject, idFieldName: getRelationIdFieldName(field) } : null;
}

/**
 * @param {{ attachment: any, onClose: () => void }} props
 */
function FilePreviewModal({ attachment, onClose }) {
  const file = fileOf(attachment);
  const extension = getFileExtension(attachment.name ?? file?.label ?? '');
  const kind = getPreviewKind(extension);
  const url = fileUrl(file);
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  return html`<div class="modal-backdrop" onMouseDown=${(event) => event.target === event.currentTarget && onClose()}>
    <div class="file-preview" role="dialog" aria-modal="true" aria-label=${attachment.name}>
      <div class="file-preview-header">
        <span class="file-preview-title">${attachment.name}</span>
        <a class="light-icon-button" href=${`${url}${url.startsWith('/rest/') ? '?download=1' : ''}`} download=${attachment.name} aria-label=${t('parity.files.download')}>
          <${Icon} name="IconDownload" size=${16} />
        </a>
        <${LightIconButton} icon="IconX" ariaLabel=${t('common.close')} onClick=${onClose} />
      </div>
      <div class="file-preview-body">
        ${kind === 'IMAGE' ? html`<img src=${url} alt=${attachment.name} />` : null}
        ${kind === 'PDF' || kind === 'TEXT' ? html`<iframe src=${url} title=${attachment.name}></iframe>` : null}
        ${kind === 'VIDEO' ? html`<video src=${url} controls></video>` : null}
        ${kind === 'AUDIO' ? html`<audio src=${url} controls></audio>` : null}
        ${kind === null
          ? html`<${EmptyState} icon=${getFileCategory(extension).icon}
              title=${getFileCategory(extension).category === 'ARCHIVE' ? t('parity.files.archiveNoPreview') : t('parity.files.noPreview')} />`
          : null}
      </div>
    </div>
  </div>`;
}

/**
 * @param {{ attachment: any, onPreview: () => void, onRenamed: (attachment: any) => void, onDelete: () => void }} props
 */
function FileRow({ attachment, onPreview, onRenamed, onDelete }) {
  const menuRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const file = fileOf(attachment);
  const name = attachment.name ?? file?.label ?? '';
  const extension = getFileExtension(name);
  const category = getFileCategory(extension);
  const url = fileUrl(file);
  const baseName = extension ? name.slice(0, -(extension.length + 1)) : name;

  const saveName = async (/** @type {string} */ nextBaseName) => {
    setIsRenaming(false);
    const trimmed = nextBaseName.trim();
    if (!trimmed || trimmed === baseName) return;
    const nextName = extension ? `${trimmed}.${extension}` : trimmed;
    const attachmentObject = objectsByNameSingular.value.get('attachment');
    if (!attachmentObject) return;
    try {
      const updated = await updateOneRecord(attachmentObject, attachment.id, { name: nextName, file: file ? [{ ...file, label: nextName }] : [] });
      onRenamed(updated ?? { ...attachment, name: nextName });
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<div class="file-row">
    <span class="file-icon" data-color=${category.color}><${Icon} name=${category.icon} size=${14} /></span>
    ${isRenaming
      ? html`<input class="text-input file-rename-input" value=${baseName} aria-label=${t('parity.files.rename')}
          ref=${(/** @type {HTMLInputElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true', requestAnimationFrame(() => element.select()))}
          onKeyDown=${(event) => {
            if (event.key === 'Enter') saveName(event.currentTarget.value);
            if (event.key === 'Escape') setIsRenaming(false);
          }}
          onBlur=${(event) => saveName(event.currentTarget.value)} />`
      : html`<a class="file-name" href=${url} onClick=${(/** @type {MouseEvent} */ event) => {
          if (event.metaKey || event.ctrlKey) return;
          event.preventDefault();
          onPreview();
        }}>${name}</a>`}
    <span class="file-date"><${Icon} name="IconCalendar" size=${14} />${formatDate(attachment.createdAt)}</span>
    <span ref=${menuRef}>
      <${LightIconButton} icon="IconDotsVertical" ariaLabel=${t('parity.files.more')} onClick=${() => setIsMenuOpen(!isMenuOpen)} />
    </span>
    <${Dropdown} anchorRef=${menuRef} isOpen=${isMenuOpen} onClose=${() => setIsMenuOpen(false)} placement="bottom-end" width=${180}>
      <${DropdownMenuItems}>
        <${MenuItem} icon="IconDownload" text=${t('parity.files.download')} onClick=${() => {
          setIsMenuOpen(false);
          const link = document.createElement('a');
          link.href = `${url}${url.startsWith('/rest/') ? '?download=1' : ''}`;
          link.download = name;
          link.click();
        }} />
        <${MenuItem} icon="IconPencil" text=${t('parity.files.rename')} onClick=${() => {
          setIsMenuOpen(false);
          setIsRenaming(true);
        }} />
        <${MenuItem} icon="IconTrash" accent="danger" text=${t('parity.files.delete')} onClick=${() => {
          setIsMenuOpen(false);
          onDelete();
        }} />
      <//>
    <//>
  </div>`;
}

/**
 * @param {{ objectNameSingular: string, recordId: string, isReadOnly?: boolean }} props
 */
export function FilesTab({ objectNameSingular, recordId, isReadOnly = false }) {
  const target = getAttachmentTargetField(objectNameSingular);
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [attachments, setAttachments] = useState(/** @type {any[]} */ ([]));
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingNames, setUploadingNames] = useState(/** @type {string[]} */ ([]));
  const [isDragging, setIsDragging] = useState(false);
  const [previewed, setPreviewed] = useState(/** @type {any | null} */ (null));

  useEffect(() => {
    if (!target) return;
    setIsLoading(true);
    findManyRecords(target.attachmentObject, {
      filter: { field: target.idFieldName, operator: 'eq', value: recordId },
      orderBy: [{ field: 'createdAt', direction: 'DescNullsFirst' }],
      limit: 200,
      depth: 0,
    })
      .then(({ records }) => setAttachments(records.filter((record) => fileOf(record)?.fileId)))
      .catch(showErrorSnackbar)
      .finally(() => setIsLoading(false));
  }, [objectNameSingular, recordId]);

  if (!target) return html`<${EmptyState} icon="IconPaperclip" title=${t('parity.files.unavailable')} />`;

  const uploadAll = async (/** @type {File[]} */ files) => {
    for (const file of files) {
      if (file.size === 0 || file.size > MAXIMUM_FILE_BYTES) {
        enqueueSnackbar({ message: t('parity.files.tooLarge', { name: file.name }), variant: 'error' });
        continue;
      }
      setUploadingNames((names) => [...names, file.name]);
      try {
        const uploaded = await uploadFile(file);
        const created = await createRecord(target.attachmentObject, {
          name: file.name,
          file: [{ fileId: uploaded.id, label: file.name, extension: getFileExtension(file.name) || null, url: uploaded.url ?? `/rest/files/${uploaded.id}` }],
          [target.idFieldName]: recordId,
        });
        setAttachments((current) => [created, ...current]);
      } catch (error) {
        showErrorSnackbar(error);
      } finally {
        setUploadingNames((names) => names.filter((name) => name !== file.name));
      }
    }
  };

  const remove = async (/** @type {any} */ attachment) => {
    const previous = attachments;
    setAttachments(attachments.filter((candidate) => candidate.id !== attachment.id));
    try {
      // destruir, como o Twenty: um documento excluído não fica na lixeira apontando para um arquivo apagado
      await api.delete(`/rest/${target.attachmentObject.namePlural}/${attachment.id}`);
      const fileId = fileOf(attachment)?.fileId;
      if (fileId) await deleteUploadedFile(fileId).catch(() => {});
      mutationCounterState.value += 1;
      showSuccessSnackbar(t('parity.files.deleted'));
    } catch (error) {
      setAttachments(previous);
      showErrorSnackbar(error);
    }
  };

  const dropHandlers = isReadOnly ? {} : {
    onDragOver: (/** @type {DragEvent} */ event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: (/** @type {DragEvent} */ event) => {
      if (!(/** @type {HTMLElement} */ (event.currentTarget)).contains(/** @type {Node | null} */ (event.relatedTarget))) setIsDragging(false);
    },
    onDrop: (/** @type {DragEvent} */ event) => {
      event.preventDefault();
      setIsDragging(false);
      uploadAll([...(event.dataTransfer?.files ?? [])]);
    },
  };

  return html`<div class="activity-tab files-tab" ...${dropHandlers}>
    <div class="activity-tab-header">
      <span class="activity-tab-title">${t('parity.files.title')}<span class="activity-tab-count">${attachments.length}</span></span>
      ${isReadOnly ? null : html`<${Button} icon="IconPlus" title=${t('parity.files.add')} size="small" variant="secondary" onClick=${() => inputRef.current?.click()} />`}
      <input ref=${inputRef} type="file" multiple hidden data-testid="files-input" onChange=${(event) => {
        uploadAll([...(event.currentTarget.files ?? [])]);
        event.currentTarget.value = '';
      }} />
    </div>
    ${isDragging
      ? html`<div class="file-drop-zone">
          <${Icon} name="IconUpload" size=${24} />
          <span class="file-drop-zone-title">${t('parity.files.dropTitle')}</span>
          <span class="file-drop-zone-subtitle">${t('parity.files.dropSubtitle')}</span>
        </div>`
      : isLoading
        ? html`<div class="file-list">${[0, 1].map((index) => html`<div key=${index} class="file-row"><${Skeleton} width="70%" height=${14} /></div>`)}</div>`
        : attachments.length === 0 && uploadingNames.length === 0
          ? html`<${EmptyState} icon="IconPaperclip" title=${t('parity.files.emptyTitle')} subtitle=${t('parity.files.emptySubtitle')}
              action=${isReadOnly ? null : html`<${Button} variant="secondary" icon="IconPlus" title=${t('parity.files.add')} onClick=${() => inputRef.current?.click()} />`} />`
          : html`<div class="file-list">
              ${uploadingNames.map((name) => html`<div key=${`uploading-${name}`} class="file-row" data-uploading="true">
                <span class="file-icon" data-color="gray"><${Icon} name="IconUpload" size=${14} /></span>
                <span class="file-name">${name}</span><span class="file-date">${t('parity.files.uploading')}</span>
              </div>`)}
              ${attachments.map((attachment) => html`<${FileRow}
                key=${attachment.id}
                attachment=${attachment}
                onPreview=${() => setPreviewed(attachment)}
                onRenamed=${(updated) => setAttachments((current) => current.map((candidate) => (candidate.id === updated.id ? updated : candidate)))}
                onDelete=${() => remove(attachment)}
              />`)}
            </div>`}
    ${previewed ? html`<${FilePreviewModal} attachment=${previewed} onClose=${() => setPreviewed(null)} />` : null}
  </div>`;
}

